import { CONFIGURATION_APIS } from "@/api_framework/api_config";
import {
  IAllStaffAttendanceRow,
  IClinicDailyAttendanceResponse,
  IDoctorAttendanceRow,
  IGroomerAttendanceSummary,
} from "@/api_framework/api_modals/Configuration";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useAPIUtils } from "@/app/hooks/useAPIUtils";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import axios from "axios";
import { useCallback, useState } from "react";
import AxiosCurlirize from "axios-curlirize";

AxiosCurlirize(axios, () => {});

interface IAttendanceState {
  doctors: IDoctorAttendanceRow[];
  groomerSummary: IGroomerAttendanceSummary | null;
  allStaff: IAllStaffAttendanceRow[];
  errors: { doctors: boolean; groomers: boolean; allStaff: boolean };
}

const EMPTY: IAttendanceState = {
  doctors: [],
  groomerSummary: null,
  allStaff: [],
  errors: { doctors: false, groomers: false, allStaff: false },
};

const FAILED: IAttendanceState = {
  ...EMPTY,
  errors: { doctors: true, groomers: true, allStaff: true },
};

export const useClinicDailyAttendanceAPI = () => {
  const configuration = useConfiguration();
  const { captureError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();
  const [state, setState] = useState<IAttendanceState>(EMPTY);
  const [isLoading, setIsLoading] = useState(false);

  const execute = useCallback(
    async (clinicId: string, date: string) => {
      if (!clinicId || !date) {
        setState(EMPTY);
        return;
      }
      setIsLoading(true);
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const { data } = await axios.get<IClinicDailyAttendanceResponse>(
          `${CONFIGURATION_APIS.CLINIC_DAILY_ATTENDANCE.baseURL}${clinicId}/?date=${date}`,
          { headers: { Authorization: `${accessToken}`, "Content-Type": "application/json" } },
        );
        const { doctors, groomers, all_staff } = data.data;
        setState({
          doctors: doctors.results ?? [],
          groomerSummary: groomers.status === "ok" ? (groomers as IGroomerAttendanceSummary) : null,
          allStaff: all_staff.results ?? [],
          errors: {
            doctors: doctors.status !== "ok",
            groomers: groomers.status !== "ok",
            allStaff: all_staff.status !== "ok",
          },
        });
      } catch (error) {
        setState(FAILED);
        if (axios.isAxiosError(error)) {
          captureError(error);
          if (error.config) sendCurl(error.config["curlCommand"]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [configuration, captureError, sendCurl],
  );

  return { execute, isLoading, ...state };
};
